import { useCallback, useEffect, useState } from "react";
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { Eye, FileText, MousePointerClick, RefreshCw, Users } from "lucide-react";
import { apiFetch } from "./api";

const Card = ({ icon: Icon, label, value, sub, testid }) => (
    <div
        data-testid={testid}
        className="rounded-2xl border border-white/10 bg-[#0C1019] p-5"
    >
        <div className="flex items-center gap-2 text-slate-500">
            <Icon className="h-4 w-4 text-sky-400" />
            <p className="font-mono text-[10px] uppercase tracking-[0.2em]">
                {label}
            </p>
        </div>
        <p className="mt-3 font-display text-3xl font-bold text-white">{value}</p>
        {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
    </div>
);

export const Analytics = ({ token, onAuthError }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        setError("");
        try {
            setData(await apiFetch("/analytics/summary", { token }));
        } catch (err) {
            if (err.status === 401) return onAuthError();
            setError(err.message);
        }
    }, [token, onAuthError]);

    useEffect(() => {
        load();
    }, [load]);

    if (!data && !error)
        return <p className="py-10 text-center text-sm text-slate-500">Loading…</p>;

    const t = data?.totals || {};
    const conv =
        t.page_views > 0
            ? `${((t.quote_opens / t.page_views) * 100).toFixed(1)}% of visits`
            : "—";

    return (
        <div data-testid="admin-analytics-tab">
            <div className="flex items-center justify-between">
                <p className="text-sm text-slate-400">Last 30 days</p>
                <button
                    data-testid="analytics-refresh-btn"
                    onClick={load}
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-white transition-colors hover:border-sky-400/50"
                >
                    <RefreshCw className="h-3.5 w-3.5" /> Refresh
                </button>
            </div>
            {error && (
                <p className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
                    {error}
                </p>
            )}

            <div className="mt-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
                <Card
                    icon={Eye}
                    label="Site Visits"
                    value={t.page_views ?? 0}
                    sub={`${t.unique_sessions ?? 0} unique visitors`}
                    testid="analytics-card-visits"
                />
                <Card
                    icon={MousePointerClick}
                    label="Quote Clicks"
                    value={t.quote_opens ?? 0}
                    sub={conv}
                    testid="analytics-card-quotes"
                />
                <Card
                    icon={Users}
                    label="Dealer Form Opens"
                    value={t.dealer_form_opens ?? 0}
                    testid="analytics-card-form-opens"
                />
                <Card
                    icon={FileText}
                    label="Dealer Leads"
                    value={t.leads_total ?? 0}
                    sub={`${t.lead_submits ?? 0} form submissions (30d)`}
                    testid="analytics-card-leads"
                />
            </div>

            <div className="mt-6 rounded-2xl border border-white/10 bg-[#0C1019] p-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    Visits vs Quote Clicks
                </p>
                <div className="mt-4 h-64" data-testid="analytics-chart">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={data?.daily || []}>
                            <CartesianGrid stroke="#232b3a" strokeDasharray="3 3" />
                            <XAxis
                                dataKey="date"
                                tick={{ fill: "#64748b", fontSize: 11 }}
                                tickFormatter={(d) => d.slice(5)}
                            />
                            <YAxis
                                allowDecimals={false}
                                tick={{ fill: "#64748b", fontSize: 11 }}
                                width={30}
                            />
                            <Tooltip
                                contentStyle={{
                                    background: "#121722",
                                    border: "1px solid #232b3a",
                                    borderRadius: 12,
                                    fontSize: 12,
                                }}
                                labelStyle={{ color: "#94a3b8" }}
                            />
                            <Area
                                type="monotone"
                                dataKey="page_views"
                                name="Visits"
                                stroke="#38bdf8"
                                fill="#38bdf8"
                                fillOpacity={0.15}
                                strokeWidth={2}
                            />
                            <Area
                                type="monotone"
                                dataKey="quote_opens"
                                name="Quote clicks"
                                stroke="#f8fafc"
                                fill="#f8fafc"
                                fillOpacity={0.05}
                                strokeWidth={2}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
                <div className="rounded-2xl border border-white/10 bg-[#0C1019] p-5">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                        Campaign Sources (UTM)
                    </p>
                    {data?.sources?.length ? (
                        <table
                            className="mt-4 w-full text-left text-sm"
                            data-testid="analytics-sources"
                        >
                            <thead>
                                <tr className="border-b border-white/10 text-xs text-slate-500">
                                    <th className="py-2">Source</th>
                                    <th className="py-2">Medium</th>
                                    <th className="py-2 text-right">Events</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.sources.map((s, i) => (
                                    <tr
                                        key={i}
                                        className="border-b border-white/5 text-slate-300"
                                    >
                                        <td className="py-2.5">{s.source}</td>
                                        <td className="py-2.5">
                                            {s.medium || "—"}
                                        </td>
                                        <td className="py-2.5 text-right">
                                            {s.count}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <p className="mt-4 text-sm text-slate-500">
                            No campaign traffic yet. Tag your ad links with
                            utm_source / utm_medium / utm_campaign and they'll show
                            up here.
                        </p>
                    )}
                </div>
                <div className="rounded-2xl border border-white/10 bg-[#0C1019] p-5">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                        Purchases
                    </p>
                    <p className="mt-4 text-sm leading-relaxed text-slate-400">
                        Completed purchases happen inside your DealerAdmin portal,
                        so they're reported there. This dashboard tracks the top of
                        the funnel: visits, quote clicks, and dealer leads. Connect
                        Google Analytics in Settings for full cross-channel
                        reporting.
                    </p>
                </div>
            </div>
        </div>
    );
};
